import React, { useEffect, useState } from 'react';
import { StepHeader } from '../ui/StepHeader.jsx';
import { StepIntro } from '../ui/StepIntro.jsx';
import { Icon } from '../ui/Icon.jsx';
import { getAcquisitionFormChargeResult } from '../utils/acquisitionFormApi.js';
import { trackPaymentFailed, trackPurchase } from '../utils/tracking.js';
import { getVocabulary } from '../utils/vocabulary.js';

const PIX_QR_CODE_BASE_URL = 'https://qr.trackmob.com.br/v1/open';
// Preview não tem cobrança para consultar: o passo do Pix renderiza a partir deste
// payload de exemplo para que o fluxo mostre o QR Code e o código copia e cola.
const PREVIEW_PIX_RESULT = {
  status: 'Pending',
  pix: {
    qrCode: '00020101021226820014br.gov.bcb.pix2560pix.stone.com.br/pix/v2/9f4c2b1e-7d3a-4f88-9c21-6b5ae0d7e4f15204000053039865802BR5925TRACKMOB TECNOLOGIA LTDA6009SAO PAULO62070503***6304A1B2'
  }
};
const SUCCESS_STATUSES = ['Paid', 'OverPaid', 'UnderPaid'];
const FAILURE_STATUSES = ['Error', 'HardBounce', 'SoftBounce', 'Canceled', 'Expired', 'Refunded', 'ChargedBack'];
const PIX_METHODS = ['Pix', 'PixAutomatic'];

export function ChargeResultStep({ config, formData, onChange, onNext, onClose, onRetryPayment, isInline, isPreview }) {
  const { Visual } = config;
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const paymentMethod = formData.paymentMethod;
  const isAnyKindPix = PIX_METHODS.includes(paymentMethod);
  const isPixAutomatic = paymentMethod === 'PixAutomatic';
  const isBoleto = paymentMethod === 'Boleto';
  const previewPix = isPreview && isAnyKindPix;
  const result = previewPix ? PREVIEW_PIX_RESULT : formData.chargeResult;
  const status = result?.status;
  const isBoletoDone = isBoleto && ['Generated', ...SUCCESS_STATUSES].includes(status);
  const isPaid = SUCCESS_STATUSES.includes(status);
  const isFailure = FAILURE_STATUSES.includes(status);
  // The submission failed before a donation was created (no charge status, just an error
  // message) — recoverable by resubmitting the donation (update endpoint).
  const submissionFailed = Boolean(result?.errorMessage) && !status;
  // Wording only: a declined card gets "try another card", any other refusal "try again".
  const ccDeclined = paymentMethod === 'CreditCard' && ['HardBounce', 'SoftBounce'].includes(status);
  const attemptsExceeded = Boolean(result?.attemptsExceeded);
  const pixAutomaticRefused = isPixAutomatic && isFailure;
  const recoverable = !attemptsExceeded && !pixAutomaticRefused && (isFailure || submissionFailed);

  const pixReady = isAnyKindPix && Boolean(result?.pix?.qrCode);
  const pixQrImageUrl = result?.pix?.qrCode
    ? `${PIX_QR_CODE_BASE_URL}/qr?code=${encodeURIComponent(result.pix.qrCode)}`
    : null;
  const boletoReady = isBoleto && Boolean(result?.boleto?.url || result?.boleto?.digitableLine);
  // Debit is scheduled (remittance to the bank) — there's nothing to poll/show.
  const isDebit = paymentMethod === 'DebitAccount';
  // Still creating/processing the charge: nothing actionable to show yet.
  const isProcessing = !error && !isDebit && !pixReady && !boletoReady && !isFailure && !submissionFailed && !(!isAnyKindPix && isPaid);

  const vocab = getVocabulary(config);
  const processingLabel = isAnyKindPix ? 'Gerando seu QR Code…'
    : isBoleto ? 'Emitindo seu boleto…'
    : `Processando ${vocab.your} ${vocab.term}…`;

  useEffect(() => {
    // Preview: no real charge to poll.
    if (isPreview) return undefined;

    // Debit charges are processed asynchronously via bank remittance; no polling.
    if (paymentMethod === 'DebitAccount') {
      trackPurchase(config, formData, 'pending');
      return undefined;
    }

    let disposed = false;
    let timeoutId;

    async function poll() {
      try {
        const chargeResult = await getAcquisitionFormChargeResult(formData.submissionId);
        if (disposed) return;

        setError('');

        onChange({ chargeResult });
        const nextStatus = chargeResult?.status;
        const boletoDone = isBoleto && ['Generated', ...SUCCESS_STATUSES].includes(nextStatus);
        const paid = SUCCESS_STATUSES.includes(nextStatus);
        const failed = FAILURE_STATUSES.includes(nextStatus);
        // Submission-level failure surfaces as an error message without a charge status.
        const submissionFailedNow = Boolean(chargeResult?.errorMessage) && !nextStatus;

        if ((!isAnyKindPix && paid) || boletoDone) {
          trackPurchase(config, formData, paid ? 'paid' : 'pending');
          return;
        }

        if (isAnyKindPix && paid) {
          trackPurchase(config, formData, 'paid');
          onNext();
          return;
        }

        if (failed || submissionFailedNow) {
          trackPaymentFailed(config, formData, nextStatus || 'SubmissionFailed');
          return;
        }

        timeoutId = window.setTimeout(poll, 3000);
      } catch (pollError) {
        if (disposed) return;

        if (pollError.status === 404) {
          setError('A consulta desta doação expirou. Recarregue a página para acompanhar o pagamento.');
          return;
        }

        setError(pollError.message || 'Não foi possível consultar a cobrança.');
        timeoutId = window.setTimeout(poll, 5000);
      }
    }

    poll();

    return () => {
      disposed = true;
      window.clearTimeout(timeoutId);
    };
  }, [formData.submissionId, paymentMethod]);

  function handleCopy(text) {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepHeader onClose={onClose} showClose={!isInline} />

      <div className="tdw-step-body">
        {isPreview && !previewPix ? (
          <>
            <PaymentMessage type="success" text="O resultado do pagamento aparece aqui após a confirmação. Esta é apenas uma pré-visualização." />
            <button type="button" className="tdw-btn-cta" onClick={onNext}>
              Continuar
            </button>
          </>
        ) : (
          <>
        {isProcessing && (
          <div className="tdw-processing">
            <Spinner color={Visual?.PrimaryColor || '#3D52D5'} />
            <p className="tdw-processing-title">{processingLabel}</p>
            <p className="tdw-processing-sub">Não feche esta página.</p>
          </div>
        )}
        {error && <PaymentMessage type="error" text={error} />}
        {pixReady && (
          <>
            <StepIntro
              title={isPixAutomatic ? 'Pague com Pix Automático' : 'Pague com Pix'}
              subtitle="Escaneie o QR Code no app do seu banco ou copie o código abaixo."
            />
            <div className="tdw-pix-qr">
              <QrCode value={pixQrImageUrl} />
              <div className="tdw-pix-copy-row">
                <span className="tdw-pix-code">{result.pix.qrCode}</span>
                <button type="button" className="tdw-copy-btn" onClick={() => handleCopy(result.pix.qrCode)}>
                  <Icon name={copied ? 'check' : 'copy'} size={16} />
                  {copied ? 'Copiado!' : 'Copiar código'}
                </button>
              </div>
              <div className="tdw-wait-badge">
                <span className="tdw-wait-badge-dot" />
                Aguardando pagamento…
              </div>
              {isPixAutomatic && (
                <p className="tdw-pix-instruction">Ao pagar, você autoriza no seu banco a cobrança automática deste valor todo mês.</p>
              )}
              <p className="tdw-pix-instruction">A confirmação aparece aqui automaticamente após o pagamento.</p>
            </div>
            {previewPix && (
              <button type="button" className="tdw-btn-cta" onClick={onNext}>
                Continuar
              </button>
            )}
          </>
        )}
        {boletoReady && (
          <>
            <div className="tdw-status">
              <span className="tdw-status-icon success"><Icon name="check" size={26} /></span>
              <h2 className="tdw-status-title">Seu boleto está pronto</h2>
              <p className="tdw-status-sub">Após o pagamento, a compensação pode levar até 3 dias úteis.</p>
            </div>
            {result.boleto.digitableLine ? (
              <div className="tdw-boleto-box">
                <span className="tdw-boleto-box-label">Linha digitável</span>
                <p className="tdw-boleto-code">{result.boleto.digitableLine}</p>
                <div className="tdw-boleto-actions">
                  <button type="button" className="tdw-copy-btn" onClick={() => handleCopy(result.boleto.digitableLine)}>
                    <Icon name={copied ? 'check' : 'copy'} size={16} />
                    {copied ? 'Copiado!' : 'Copiar linha digitável'}
                  </button>
                  {result.boleto.url && (
                    <a className="tdw-btn-outline" href={result.boleto.url} target="_blank" rel="noreferrer">
                      Abrir boleto
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="tdw-boleto-actions">
                <a className="tdw-btn-outline" href={result.boleto.url} target="_blank" rel="noreferrer">
                  Abrir boleto
                </a>
              </div>
            )}
          </>
        )}
        {(isFailure || submissionFailed) && (
          <>
            <div className="tdw-status">
              <span className="tdw-status-icon error"><Icon name="close" size={26} /></span>
              <h2 className="tdw-status-title">O pagamento não foi aprovado</h2>
              <p className="tdw-status-sub">
                {result?.errorMessage || (ccDeclined
                  ? 'Não foi possível aprovar o cartão. Tente com outro cartão.'
                  : 'Não foi possível confirmar o pagamento.')}
              </p>
            </div>
            {recoverable && onRetryPayment && (
              <button type="button" className="tdw-btn-cta" onClick={onRetryPayment}>
                {ccDeclined ? 'Tentar com outro cartão' : 'Tentar novamente'}
              </button>
            )}
            {pixAutomaticRefused && (
              <PaymentMessage
                type="error"
                text="Para tentar de novo, recarregue a página e preencha o formulário novamente."
              />
            )}
            {attemptsExceeded && (
              <PaymentMessage
                type="error"
                text="Você atingiu o limite de tentativas de pagamento desta doação. Recarregue a página para começar uma nova."
              />
            )}
          </>
        )}
        {isDebit && (
          <>
            <div className="tdw-status">
              <span className="tdw-status-icon success"><Icon name="check" size={26} /></span>
              <h2 className="tdw-status-title">Débito em conta agendado</h2>
              <p className="tdw-status-sub">A cobrança será feita na data de vencimento.</p>
            </div>
            <button type="button" className="tdw-btn-cta" onClick={onNext}>
              Continuar
            </button>
          </>
        )}
        {!isAnyKindPix && !isBoleto && isPaid && (
          <>
            <div className="tdw-status">
              <span className="tdw-status-icon success"><Icon name="check" size={26} /></span>
              <h2 className="tdw-status-title">Pagamento aprovado!</h2>
              <p className="tdw-status-sub">Você já está fazendo a diferença.</p>
            </div>
            <button type="button" className="tdw-btn-cta" onClick={onNext}>
              Continuar
            </button>
          </>
        )}
        {isBoletoDone && (
          <button type="button" className="tdw-btn-cta" onClick={onNext}>
            Continuar
          </button>
        )}
          </>
        )}
      </div>
    </div>
  );
}

function Spinner({ color }) {
  // Inline SVG (SMIL) so the spinner animates without depending on stylesheet keyframes.
  return (
    <svg width="44" height="44" viewBox="0 0 50 50" role="status" aria-label="Processando">
      <circle cx="25" cy="25" r="20" fill="none" stroke="#E5E7EB" strokeWidth="5" />
      <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray="80 200">
        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function PaymentMessage({ type, text }) {
  return <div className={`tdw-payment-message ${type}`}>{text}</div>;
}

function QrCode({ value }) {
  if (/^(data:image\/|https?:\/\/)/.test(value)) {
    return <img className="tdw-qr-image" src={value} alt="QR Code Pix" />;
  }

  return <div className="tdw-qr-code">{value}</div>;
}
